import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { ColorHex, ColorRgb, isValidHexColor, negativeBlackOrWhiteHex, rgbToHex } from "@cervval.js/common/src/tools/color.tools";
import { CjsColorPicker, ColorPickerOptions } from "../../../pickers/colorPicker/colorPicker";
import { EditorWithPicker, EditorWithPickerOptions, defaultEditorWithPickerOptions } from "../editorWithPicker";


export interface ColorEditorOptions extends EditorWithPickerOptions {
    defaultColor?: ColorHex;
}

export const defaultColorEditorPickerOptions: ColorEditorOptions = {
    ...defaultEditorWithPickerOptions,
    popupMode: "direct",
    buttonIcon: { name: "fa-eye-dropper" }
};

@CustomElement({
    name: "cjs-color-editor",
    elementCssClasses: ["cjs-editor"]
})
export class CjsColorEditor extends EditorWithPicker<ColorHex> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultColorEditorPickerOptions)
    public readonly options!: WebComponentOptions<ColorEditorOptions>;

    /**
     * The ColorPicker used to edit the value of the input
     */
    protected _picker = new CjsColorPicker();

    /** Shortcut for picker options */
    public get pickerOptions(): WebComponentOptions<ColorPickerOptions> {
        return this._picker.options;
    }

    constructor(options?: Partial<ColorEditorOptions>, datePickerOptions?: Partial<ColorPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.pickerOptions.setWithoutRefresh(datePickerOptions);
    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsColorPicker {
        if (this._value === undefined) {
            this._picker = new CjsColorPicker(this.pickerOptions);
        }
        this._picker.setValue(this._value ?? this.options.defaultColor);
        this._picker.refresh();
        return this._picker;
    }

    /**
     * Format the value to a string which can be display for user
     */
    public convertValueToInputValue(value: string | undefined): string {
        return value ?? "";
    }

    /**
     * Update the value of the input from _value of the Editor
     * @override
     */
    protected override _refreshInputValue(): void {

        const backgroundColor = this._value ?? this.options.defaultColor;
        const textColor = backgroundColor ? negativeBlackOrWhiteHex(backgroundColor) : undefined;
        // Refresh backgroundColor of inputWithButton button
        this._inputWithButton.button.options.setWithoutRefresh({
            backgroundColor,
            textColor
        });

        super._refreshInputValue();
    }

    /**
     * Return the displayed value in correct hexa color
     */
    protected _convertInputValueToValue(displayedValue: string | undefined): ColorHex | undefined {
        if (displayedValue == null) { return undefined; }

        let hexValue: ColorHex | undefined;
        // Check if the given color is a rgb format rgb(X,Y,Z)
        if (!isValidHexColor(displayedValue)) {
            hexValue = rgbToHex(displayedValue as ColorRgb);
        } else { hexValue = displayedValue; }

        // Check if the given color is valid, than update the picker
        if (hexValue != null && isValidHexColor(hexValue)) {
            return hexValue;
        } else {
            this._log?.warn("Color value: " + hexValue + " isn't a valid format");
            return undefined;
        }
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): ColorHex | undefined {
        const inputValue = this.getInput().components.inputText.value;
        return this._convertInputValueToValue(inputValue);
    }

}